import { useCallback, useEffect, useState } from "react";
import { Loader2, Pencil, Search, Trash2 } from "lucide-react";
import { useAuth } from "../../auth/AuthContext";
import { log } from "../../lib/log";
import { Card, METHODS } from "./_shared";
import { EditMerchantModal } from "./EditMerchantModal";

// ---------------------------------------------------------------------------
// Browse Tab — search, filter, paginated list with per-row Edit / Delete
// ---------------------------------------------------------------------------
export const FILTERS = [
  { key: "",       label: "All" },
  { key: "amex",   label: "Amex" },
  { key: "klarna", label: "Klarna" },
  { key: "sas",    label: "SAS" },
  { key: "trumf",  label: "Trumf" },
  { key: "auto",   label: "Auto" },
  { key: "manual", label: "Manual" },
];

export function BrowseTab({ onChange }) {
  const { apiFetch } = useAuth();
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("");
  const [methodFilter, setMethodFilter] = useState("");
  const [methodOptions, setMethodOptions] = useState([]);
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState({});
  const limit = 25;

  // Load distinct payment methods once for the dropdown
  useEffect(() => {
    let cancelled = false;
    apiFetch("/api/admin/payment-methods")
      .then((d) => { if (!cancelled) setMethodOptions(d.methods || []); })
      .catch((e) => log.warn("[admin] method options load:", e?.message));
    return () => { cancelled = true; };
  }, [apiFetch]);

  const load = useCallback(async () => {
    setItems(null);
    try {
      const params = new URLSearchParams({
        q, filter, method: methodFilter, limit: String(limit), offset: String(offset),
      });
      const d = await apiFetch(`/api/merchants/search?${params}`);
      setItems(d.items || []); setTotal(d.total || 0);
    } catch { setItems([]); setTotal(0); }
  }, [apiFetch, q, filter, methodFilter, offset]);

  useEffect(() => { load(); }, [load]);

  // Debounced search: reset offset when query changes
  useEffect(() => {
    const t = setTimeout(() => setOffset(0), 0);
    return () => clearTimeout(t);
  }, [q, filter, methodFilter]);

  const removeMerchant = async (host) => {
    if (!confirm(`Delete ${host}?`)) return;
    setBusy((b) => ({ ...b, [host]: true }));
    try {
      await apiFetch(`/api/merchants/${encodeURIComponent(host)}`, { method: "DELETE" });
      load(); onChange?.();
    } catch (e) { log.warn("[admin] merchant delete failed:", e?.message); }
    finally { setBusy((b) => ({ ...b, [host]: false })); }
  };

  const onSaved = () => { setEditing(null); load(); onChange?.(); };

  return (
    <div className="space-y-px">
      <Card title="BROWSE & EDIT MERCHANTS"
            subtitle="Search by host, alias or name. Click Edit to change methods, bonus details, or name. Delete to remove entirely."
            accent="#A78BFA" testid="browse-card">
        <div className="flex flex-wrap gap-3 items-stretch mb-4">
          <div className="relative flex-1 min-w-[260px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--srs-ink-faint)] pointer-events-none" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="komplett, lego, asos.com…"
              data-testid="browse-search-input"
              className="w-full bg-[var(--srs-surface-2)] border border-[var(--srs-line)] focus:border-[#A78BFA] outline-none pl-10 pr-4 py-3 text-sm text-[var(--srs-ink)] placeholder:text-[var(--srs-ink-faint)] font-mono transition-colors"
            />
          </div>
          <select
            value={methodFilter}
            onChange={(e) => setMethodFilter(e.target.value)}
            data-testid="browse-method-select"
            className="bg-[var(--srs-surface-2)] border border-[var(--srs-line)] focus:border-[#A78BFA] outline-none px-3 py-3 text-sm text-[var(--srs-ink)] min-w-[180px]"
          >
            <option value="">All payment methods</option>
            {methodOptions.map((m) => (
              <option key={m.method} value={m.method}>{m.method} ({m.merchant_count})</option>
            ))}
          </select>
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="browse-filter">
          {FILTERS.map((f) => (
            <button key={f.key || "all"} onClick={() => setFilter(f.key)}
              data-testid={`browse-filter-${f.key || "all"}`}
              className={`px-3 py-1.5 text-[10px] tracking-[0.2em] font-bold uppercase border transition-colors ${filter === f.key ? "text-black bg-[#A78BFA] border-[#A78BFA]" : "text-[var(--srs-ink-dim)] border-[var(--srs-line)] hover:text-[var(--srs-ink)]"}`}>
              {f.label}
            </button>
          ))}
          <span className="ml-auto text-[10px] tracking-[0.2em] text-[var(--srs-ink-faint)] font-bold" data-testid="browse-total">
            {total.toLocaleString()} result{total === 1 ? "" : "s"}
          </span>
        </div>

        {items === null && (
          <div className="text-xs text-[var(--srs-ink-faint)] flex items-center gap-2 py-8 justify-center">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
          </div>
        )}
        {items?.length === 0 && (
          <div className="text-xs text-[var(--srs-ink-faint)] py-8 text-center" data-testid="browse-empty">
            No merchants match this search.
          </div>
        )}

        <div className="flex flex-col gap-2" data-testid="browse-list">
          {items?.map((m) => (
            <MerchantRow
              key={m.host}
              merchant={m}
              busy={!!busy[m.host]}
              onEdit={() => setEditing(m)}
              onDelete={() => removeMerchant(m.host)}
            />
          ))}
        </div>

        {total > limit && (
          <div className="flex items-center justify-between mt-5 pt-4 border-t border-[var(--srs-line-soft)]" data-testid="browse-pagination">
            <button onClick={() => setOffset((o) => Math.max(0, o - limit))} disabled={offset === 0}
              data-testid="browse-prev"
              className="px-3 py-1.5 text-[10px] tracking-[0.2em] font-bold uppercase border border-[var(--srs-line)] hover:border-[#A78BFA] hover:text-[var(--srs-ink)] text-[var(--srs-ink-dim)] disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
              ← Prev
            </button>
            <span className="text-[10px] tracking-[0.2em] text-[var(--srs-ink-faint)] font-bold">
              {offset + 1}–{Math.min(offset + limit, total)} of {total.toLocaleString()}
            </span>
            <button onClick={() => setOffset((o) => o + limit)} disabled={offset + limit >= total}
              data-testid="browse-next"
              className="px-3 py-1.5 text-[10px] tracking-[0.2em] font-bold uppercase border border-[var(--srs-line)] hover:border-[#A78BFA] hover:text-[var(--srs-ink)] text-[var(--srs-ink-dim)] disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
              Next →
            </button>
          </div>
        )}
      </Card>

      {editing && (
        <EditMerchantModal
          merchant={editing}
          onClose={() => setEditing(null)}
          onSaved={onSaved}
        />
      )}
    </div>
  );
}

export function MerchantRow({ merchant, busy, onEdit, onDelete }) {
  const programs = merchant.programs || {};
  const hasSas = !!programs.sas;
  const hasTrumf = !!programs.trumf;
  const verdict = merchant.verdict || (merchant.methods?.includes("amex") ? "direct" : "indirect");

  return (
    <div className="border border-[var(--srs-line)] bg-[var(--srs-surface-2)] p-4 flex flex-wrap items-start gap-4" data-testid={`merchant-row-${merchant.host}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono text-sm text-[var(--srs-ink)] truncate" data-testid={`merchant-host-${merchant.host}`}>{merchant.host}</span>
          {merchant.name && <span className="text-xs text-[var(--srs-ink-dim)] truncate">· {merchant.name}</span>}
        </div>
        <div className="mt-1 text-[10px] text-[var(--srs-ink-faint)] tracking-wide flex flex-wrap gap-x-3 gap-y-1">
          {merchant.category && <span>📁 {merchant.category}</span>}
          {merchant.source && <span>src: {merchant.source}</span>}
          {merchant.aliases?.length > 0 && <span>aliases: {merchant.aliases.join(", ")}</span>}
        </div>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {(merchant.methods || []).map((mk) => {
            const m = METHODS.find((x) => x.key === mk) || { label: mk, color: "#52525B" };
            return (
              <span key={mk}
                className="px-2 py-0.5 text-[9px] tracking-[0.18em] font-bold uppercase border"
                style={{ borderColor: m.color, color: m.key === "klarna" || m.key === "applepay" ? "#fff" : m.color, background: m.key === "klarna" || m.key === "applepay" ? m.color : "transparent" }}>
                {m.label}
              </span>
            );
          })}
          {hasSas && (
            <span className="px-2 py-0.5 text-[9px] tracking-[0.18em] font-bold uppercase border border-[#6A9CFF] text-[#6A9CFF]"
              title={programs.sas.points_text}>SAS · {programs.sas.points_text || "—"}</span>
          )}
          {hasTrumf && (
            <span className="px-2 py-0.5 text-[9px] tracking-[0.18em] font-bold uppercase border border-[#C4262E] text-[#C4262E]"
              title={programs.trumf.bonus_text}>Trumf · {programs.trumf.bonus_text || "—"}</span>
          )}
          {verdict === "direct" && (merchant.methods || []).includes("amex") && (
            <span className="px-2 py-0.5 text-[9px] tracking-[0.18em] font-bold uppercase bg-[rgba(16,185,129,0.10)] text-[#10B981] border border-[rgba(16,185,129,0.35)]">DIRECT</span>
          )}
        </div>
      </div>
      <div className="flex gap-2 shrink-0">
        <button onClick={onEdit} disabled={busy}
          data-testid={`merchant-edit-${merchant.host}`}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] tracking-[0.2em] font-bold uppercase border border-[var(--srs-line)] hover:border-[#A78BFA] hover:text-[#A78BFA] text-[var(--srs-ink-dim)] transition-colors">
          <Pencil className="w-3 h-3" /> Edit
        </button>
        <button onClick={onDelete} disabled={busy}
          data-testid={`merchant-delete-${merchant.host}`}
          className="inline-flex items-center justify-center w-8 h-8 border border-[var(--srs-line)] hover:border-[#EF4444] hover:text-[#EF4444] text-[var(--srs-ink-dim)] transition-colors disabled:opacity-40">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
